import { useLazyQuery } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client/errors/ApolloError';
import gql from 'graphql-tag';
import { useCallback } from 'react';

export interface IFolder {
  'folderId':{
    'native': number;
    'partner'?: number;
  },
  'parentId'?:{
    'native': number;
    'partner'?: number;
  },
  'projectId':{
    'native': number;
    'partner'?: number;
  },
  'name':string;
  'links':{
    'project': string;
    'self': string;
    'children': string;
    'parent': string;
  }
}

interface IFolderList {
    'count': number;
    'offset': number;
    'limit': number;
    'hasMore': boolean;
    'requestedFields': string;
    'sortByName': boolean;
    'ascendingOrder': boolean;
    'items': IFolder[];
    'links': {
      'parent': string;
    }
}

interface IFolderListResponse {
  folderList: IFolderList;
}

export interface IFolderListPayload {
  called: boolean;
  loading: boolean;
  error?: ApolloError;
  folderList?: IFolderList;
}
export interface IQueryFolderListHook {
  folderListResult: IFolderListPayload;
  getFolderList: (
    folderPath?: string,
    projectId?: number,
    sortByName?: boolean,
    ascendingOrder?: boolean
  ) => void;
}

export const FOLDER_LIST_QUERY = gql`
  query folderListQuery {
    folderList(projectId: $projectId, folderPath: $folderPath, sortByName: $sortByName, ascendingOrder: $ascendingOrder)
      @rest(type: "folderList", path: "{args.folderPath}?projectId={args.projectId}&limit=1000&sortByName=true&ascendingOrder={args.ascendingOrder}") {
        count
        hasMore
        items
        limit
        links
        offset
        requestedFields
      }
  }`;

const useQueryFolderList = (): IQueryFolderListHook => {
  const [executeQuery, {
    data, error, loading, called,
  }] = useLazyQuery<IFolderListResponse>(
    FOLDER_LIST_QUERY, { notifyOnNetworkStatusChange: true, fetchPolicy: 'cache-and-network' },
  );

  const getFolderList = useCallback(
    (folderPath: string = '/folders', projectId?: number, sortByName = true, ascendingOrder = true) => executeQuery({
      variables: {
        projectId,
        folderPath,
        sortByName,
        ascendingOrder,
      },
    }), [executeQuery],
  );

  return {
    folderListResult: {
      called,
      error,
      loading,
      folderList: data ? data.folderList : undefined,
    },
    getFolderList,
  };
};

export default useQueryFolderList;
